import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, Check, Info, Undo2, X } from "lucide-react";
import { t } from "@/lib/i18n";

export type ToastTone = "success" | "danger" | "warn" | "info";

export type ToastOptions = {
  /** Main line. Keep it short and past-tense: "Stage changed". */
  title: string;
  /** Optional second line with the specifics: which candidate, which stage. */
  description?: string;
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. Undo toasts get a longer default. */
  duration?: number;
  /** Renders an Undo button and keeps the toast alive longer. */
  onUndo?: () => void;
};

type ToastRecord = ToastOptions & { id: number; tone: ToastTone; duration: number };

type ToastApi = {
  toast: (options: ToastOptions) => number;
  success: (title: string, description?: string) => number;
  error: (title: string, description?: string) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const toneIcon = { success: Check, danger: AlertTriangle, warn: AlertTriangle, info: Info } as const;

/**
 * Toasts are the only place mutations report back, so they must survive route
 * changes: the provider sits above the router and keeps its own timers.
 * Hovering or focusing a toast pauses its countdown — long error text and the
 * Undo button used to vanish under the cursor mid-read.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([]);
  const [leaving, setLeaving] = useState<Set<number>>(new Set());
  const timers = useRef(new Map<number, number>());
  const paused = useRef(new Set<number>());
  const remaining = useRef(new Map<number, number>());
  const startedAt = useRef(new Map<number, number>());
  const lastSignature = useRef<{ text: string; at: number }>({ text: "", at: 0 });
  const nextId = useRef(1);

  const removeNow = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    remaining.current.delete(id);
    startedAt.current.delete(id);
    paused.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const beginExit = useCallback((id: number) => {
    setLeaving((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      return next;
    });
    window.setTimeout(() => {
      removeNow(id);
      setLeaving((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }, 160);
  }, [removeNow]);

  const schedule = useCallback((item: ToastRecord, delay: number) => {
    startedAt.current.set(item.id, Date.now());
    const timer = window.setTimeout(() => beginExit(item.id), delay);
    timers.current.set(item.id, timer);
  }, [beginExit]);

  const pause = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (!timer || paused.current.has(id)) return;
    window.clearTimeout(timer);
    paused.current.add(id);
    const elapsed = Date.now() - (startedAt.current.get(id) ?? Date.now());
    const left = Math.max(600, (remaining.current.get(id) ?? 0) - elapsed);
    remaining.current.set(id, left);
  }, []);

  const resume = useCallback((id: number) => {
    if (!paused.current.has(id)) return;
    paused.current.delete(id);
    schedule({ id } as ToastRecord, remaining.current.get(id) ?? 1200);
  }, [schedule]);

  const dismiss = useCallback((id: number) => {
    beginExit(id);
  }, [beginExit]);

  const toast = useCallback((options: ToastOptions) => {
    const tone = options.tone ?? "success";
    // Collapse identical bursts (retry storms fired four identical errors).
    const signature = `${tone}:${options.title}:${options.description ?? ""}`;
    if (signature === lastSignature.current.text && Date.now() - lastSignature.current.at < 800) {
      return -1;
    }
    lastSignature.current = { text: signature, at: Date.now() };
    const id = nextId.current++;
    const duration = options.duration ?? (options.onUndo ? 9000 : tone === "danger" ? 7000 : 4500);
    remaining.current.set(id, duration);
    setItems((current) => [...current.slice(-3), { ...options, id, tone, duration }]);
    schedule({ ...options, id, tone, duration }, duration);
    return id;
  }, [schedule]);

  const api = useMemo<ToastApi>(() => ({
    toast,
    success: (title, description) => toast({ title, description, tone: "success" }),
    error: (title, description) => toast({ title, description, tone: "danger" }),
    dismiss,
  }), [toast, dismiss]);

  useEffect(() => () => { for (const timer of timers.current.values()) window.clearTimeout(timer); }, []);

  return <ToastContext.Provider value={api}>
    {children}
    {/* The live region is permanent so assistive tech reliably announces
        items appended into it later. */}
    <div className="toast-stack" role="region" aria-label={t("toast_region_label")} aria-live="polite">
      {items.map((item) => {
        const Icon = toneIcon[item.tone];
        // Deliberately not reusing the legacy `.toast` class: its `.toast button`
        // rule forces display:grid and collapses the Undo control.
        return <div
          key={item.id}
          className={`toast-item ${item.tone}${leaving.has(item.id) ? " leaving" : ""}`}
          role={item.tone === "danger" ? "alert" : undefined}
          onMouseEnter={() => pause(item.id)}
          onMouseLeave={() => resume(item.id)}
          onFocus={() => pause(item.id)}
          onBlur={() => resume(item.id)}
        >
          <span className={`toast-icon ${item.tone}`}><Icon className="ui-icon"/></span>
          <span className="toast-copy">
            <strong>{item.title}</strong>
            {item.description ? <small>{item.description}</small> : null}
          </span>
          {item.onUndo ? <button className="toast-undo" type="button" onClick={() => {
            try { item.onUndo?.(); }
            finally { dismiss(item.id); }
          }}><Undo2 className="ui-icon"/>{t("btn_undo")}</button> : null}
          <button className="toast-close" type="button" aria-label={t("btn_close")} onClick={() => dismiss(item.id)}><X className="ui-icon"/></button>
        </div>;
      })}
    </div>
  </ToastContext.Provider>;
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside ToastProvider");
  return context;
}
